import React from 'react';
import { useState } from 'react';

import { List, Modal, Tooltip } from 'antd';
import _ from 'lodash';

import styled from 'styled-components';

import { getBookCover } from '@libs/NwUtils';

import { useLayoutContext } from '@contexts/LayoutContext';
import { useUserPermission } from '@contexts/UserContext';

import { useModelDetailsBooks } from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsBooks/ModelDetailsBooksContext';

import AddBookToPackageButton from '@components/ActivePackages/AddBookToPackageButton';
import CreatedBy from '@components/CreatedBy/CreatedBy';
import AvatarBg from '@components/Gui/AvatarBg';
import MiniLoading from '@components/Gui/MiniLoading';
import NwEllipsis from '@components/Gui/NwEllipsis';
import NwIcon from '@components/Gui/NwIcon';
import PrintPlaylistButton from '@components/Playlists/PrintPlaylistButton';

import {
    renderBookItemFastPackageRemoveActions,
    renderBookItemPackageSectionActions,
    renderBookItemStandardActions,
} from './BookItemButtonActions';
import BookItemTitle from './BookItemTitle';
import { getBookLinkedWebsites } from './BookItemUtils';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const ListItem = styled(List.Item)`
    padding: 0.5rem 1rem;
    align-items: flex-start;

    .ant-list-item-action {
        margin-left: 0 !important;
    }
    .ant-list-item-meta-avatar {
        cursor: pointer;
    }
    cursor: ${(props) => (props.$embeddedInSortableRow ? 'grab' : 'pointer')};

    /* Nello scope package tutta la riga è il bersaglio dell'aggiunta: si evidenzia e
       scopre il segno sulla cover. La cella delle azioni è esclusa perché lì il click
       fa un'altra cosa (anteprima). */
    ${(props) =>
        props.$packageMode &&
        `
        &:hover:not(:has(.book-title-right:hover)) {
            background-color: ${props.theme.SelectedBackgroundColor};

            .package-cover-overlay {
                opacity: 1;
            }
        }
    `}

    /* Su mobile le azioni scendono su una riga propria sotto il contenuto.
       Serve display block e non una colonna flex: antd dà flex:1 a .ant-list-item-meta,
       che in colonna diventa crescita verticale e allunga l'item con spazio vuoto. */
    ${(props) =>
        props.$isMobile &&
        `
        display: block;
    `}
`;

/** Riga azioni mobile: menu a sinistra, pulsanti di stampa e package a destra. */
const MobileActionsRow = styled.div`
    margin-top: 0.75rem;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .icons-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 2rem;
        font-size: 1.3rem;
    }
`;

const BookItemContentWrapper = styled.div`
    display: flex;
    flex-direction: ${(props) => (props.$isMobile ? 'column' : 'row')};
    align-items: ${(props) => (props.$isMobile ? 'stretch' : 'flex-end')};
    justify-content: space-between;
    gap: ${(props) => (props.$isMobile ? '0.5rem' : '1.5rem')};
    width: 100%;
    padding-right: ${(props) => (props.$isMobile ? '0' : '1rem')};

    .book-item-content-left {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: flex-start;
        padding-top: 0.25rem;
        ${(props) => props.$minHeight != null && `min-height: ${props.$minHeight}px;`}

        .description-container {
            /* Su mobile niente spazio riservato: senza descrizione la riga resta vuota */
            min-height: ${(props) => (props.$isMobile ? '0' : '2rem')};
            font-size: ${(props) => props.theme.FontSizeSm};
        }

        .author-container {
            font-size: 0.6rem;
            text-transform: uppercase;
            margin: 0.25rem 0;
            display: flex;
            align-items: center;
            color: ${(props) => (props.$isPrivate ? props.theme.DangerColor : props.theme.TextColorMuted)};

            svg {
                margin-right: 0.25rem;
            }
        }
    }

    .book-item-content-right {
        /* Le icone stanno nella description di List.Item.Meta, che antd colora
           con text-color-secondary: qui serve il colore testo pieno. */
        color: ${(props) => props.theme.TextColor};
        display: flex;
        flex-wrap: nowrap;
        align-items: flex-end;
        justify-content: center;
        gap: 0.75rem;
        padding-bottom: 0.5rem;
        /* Uniforma le icone: senza questo il Badge di antd resetta il colore ereditato dalla description */
        color: ${(props) => props.theme.TextColor};

        /* FontAwesome dimensiona in em (height: 1em) e size="lg" aggiunge un 1.25em:
           fissando il font-size sull'svg si ignorano sia il moltiplicatore sia il reset di .ant-badge. */
        .svg-inline--fa {
            font-size: 1.5rem;
        }
    }
`;

const AvatarLoadingContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 80px;
    height: 80px;
    font-size: 2rem;
`;

/** Cover con il segno di aggiunta/rimozione che compare in hover sulla riga. */
const PackageCoverWrapper = styled.div`
    position: relative;
    display: inline-flex;
    line-height: 0;
    cursor: pointer;

    /* Di suo il check di AvatarBg sborda sopra la cover e finisce tagliato
       dall'overflow hidden del meta: qui si appoggia dentro l'angolo, come un badge. */
    .sup-icon {
        top: 2px;
        left: 2px;
        z-index: 1;
        border-radius: 50%;
        box-shadow: 0 0 0 1px #fff;
    }

    .package-cover-overlay {
        z-index: 2;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 2rem;
        color: #fff;
        background-color: ${(props) => (props.$isInPackage ? props.theme.DangerColor : props.theme.PrimaryColor)}E6;
        opacity: 0;
        transition: opacity 0.15s ease-in-out;
    }
`;

const EmptyBookAvatar = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${(props) => props.theme.BorderColor};
    width: ${(props) => props.$size}px;
    height: ${(props) => props.$size}px;
    font-size: 4rem;
`;

/** Keeps preview/remove clicks local; row drag uses the parent drag handle. */
const stopSortableDragSensor = (e) => {
    e.stopPropagation();
};

/** Cover della riga compatta: fissa, non derivata dalla dimensione piena. */
const COMPACT_COVER_SIZE = 60;

const BookItem = ({
    book,
    compact = false,
    showFastPackageOnlyDetails = false,
    embeddedInSortableRow = false,
    sortableDragging = false,
}) => {
    const { actions, packageSection, readOnly, websiteData, scope } = useModelDetailsBooks();
    const { isMobile } = useLayoutContext();

    const baseDrawerSize = scope === 'books-drawer' ? 140 : isMobile ? 80 : 96;
    // In compact la riga si stringe: cover fissa a 60px, titolo più piccolo, niente descrizione
    const baseSize = compact ? COMPACT_COVER_SIZE : baseDrawerSize;
    const avatarSize = showFastPackageOnlyDetails && scope === 'books-drawer' ? 110 : baseSize;
    const itemHeight = showFastPackageOnlyDetails && scope === 'books-drawer' ? Math.max(avatarSize, 120) : baseSize;

    const sortableNoDragProps = embeddedInSortableRow
        ? {
              onMouseDown: stopSortableDragSensor,
              onTouchStart: stopSortableDragSensor,
              onPointerDown: stopSortableDragSensor,
          }
        : {};

    const wrapAvatarForSortable = (inner) =>
        embeddedInSortableRow ? (
            <span style={{ cursor: 'pointer', display: 'inline-flex', lineHeight: 0 }} {...sortableNoDragProps}>
                {inner}
            </span>
        ) : (
            inner
        );

    const descriptionMinHeight = null;
    const descriptionRows =
        showFastPackageOnlyDetails && scope === 'books-drawer' ? 2 : scope === 'books-drawer' ? 3 : 2;
    const actionsHeight = itemHeight - 5;

    const [isAdding, setIsAdding] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);

    const isInBooksDrawer = scope === 'books-drawer';

    const userPermission = useUserPermission('DeleteBooks', [book.UserCreated]);
    const canPublishBooks = useUserPermission('PublishBooks');

    let isEmptyBook = false;
    if (typeof book.DocumentPlaylistsCount !== 'undefined') {
        isEmptyBook = book.DocumentPlaylistsCount === 0;
    } else if (typeof book.DocumentPlaylists !== 'undefined') {
        isEmptyBook = book.DocumentPlaylists.length === 0;
    }

    let hasCover = false;
    if (book.Covers && book.Covers.length > 0) {
        const standardcover = _.find(book.Covers, { CoverFormat: 'Standard' });
        if (standardcover) {
            hasCover = true;
        }
    }

    const isInPackage = packageSection.active && packageSection.playlists.find((playlistId) => playlistId === book.ID);

    const packageEditOnClick = async () => {
        if (!isInPackage) {
            setIsAdding(true);
            await packageSection.onAdd(book);
            setIsAdding(false);
        } else {
            setIsRemoving(true);
            await packageSection.onRemove(book);
            setIsRemoving(false);
        }
    };

    const handleBookClick = () => {
        const mediaCount =
            typeof book?.DocumentPlaylistsCount === 'number'
                ? book.DocumentPlaylistsCount
                : Array.isArray(book?.DocumentPlaylists)
                  ? book.DocumentPlaylists.length
                  : 0;
        if (packageSection.active) {
            packageEditOnClick();
        } else if (mediaCount > 0) {
            actions.onOpenBookPreviewDrawer(book);
        } else {
            Modal.error({
                title: 'Empty Book',
                content: 'This book has no images/videos.',
                centered: true,
            });
        }
    };

    // "add book to <package> / <gruppo>": dice dove il book sta per finire.
    const packageDestination = [packageSection.name, packageSection.group].filter(Boolean).join(' / ');
    const packageActionLabel = isInPackage
        ? `remove book from ${packageDestination || 'package'}`
        : `add book to ${packageDestination || 'package'}`;

    const linkedSections = getBookLinkedWebsites(book, websiteData);
    const exist = packageSection.playlists
        ? packageSection.playlists.find((playlistId) => playlistId === book.ID)
        : false;

    const getBookAvatar = () => {
        if (isAdding || isRemoving) {
            return wrapAvatarForSortable(
                <AvatarLoadingContainer>
                    <MiniLoading />
                </AvatarLoadingContainer>
            );
        }
        if (isEmptyBook) {
            return wrapAvatarForSortable(
                <EmptyBookAvatar onClick={handleBookClick} style={{ cursor: 'pointer' }} $size={avatarSize}>
                    <NwIcon icon={byPrefixAndName.fasl['empty-set']} />
                </EmptyBookAvatar>
            );
        }
        if (packageSection.active) {
            return wrapAvatarForSortable(
                <Tooltip title={packageActionLabel}>
                    <PackageCoverWrapper $isInPackage={Boolean(isInPackage)} onClick={handleBookClick}>
                        <AvatarBg size={avatarSize} src={getBookCover(book)} check={isInPackage} />
                        <span className="package-cover-overlay">
                            <NwIcon
                                icon={
                                    isInPackage ? byPrefixAndName.fasl['xmark'] : byPrefixAndName.fasl['plus']
                                }
                            />
                        </span>
                    </PackageCoverWrapper>
                </Tooltip>
            );
        }
        return wrapAvatarForSortable(
            <AvatarBg
                onClick={handleBookClick}
                style={{ cursor: 'pointer' }}
                size={avatarSize}
                src={getBookCover(book)}
                check={isInPackage}
            />
        );
    };

    const listActions = packageSection.active
        ? renderBookItemPackageSectionActions({
              onPreview: () => actions.onOpenBookPreviewDrawer(book),
          })
        : readOnly
          ? []
          : showFastPackageOnlyDetails
            ? renderBookItemFastPackageRemoveActions({
                  actionsHeight,
                  isEmptyBook,
                  sortableNoDragProps,
                  book,
                  onRemoveFastPackage: (b) => actions.onSetFastPackage(b, false),
              })
            : renderBookItemStandardActions({
                  actionsHeight,
                  book,
                  isEmptyBook,
                  hasCover,
                  userPermission,
                  canPublishBooks,
                  actions,
                  isMobile,
              });

    const bookButtons =
        !isEmptyBook && !readOnly && !showFastPackageOnlyDetails ? (
            <>
                <PrintPlaylistButton playlistID={book.ID} modelID={book.ContactID} placement="leftTop" />
                <AddBookToPackageButton book={book} placement="leftTop" />
            </>
        ) : null;

    return (
        <ListItem
            $isInBooksDrawer={isInBooksDrawer}
            $embeddedInSortableRow={embeddedInSortableRow}
            $isMobile={isMobile}
            $packageMode={packageSection.active}
            // actions={listActions}
            className="ant-list-item-top"
        >
            <List.Item.Meta
                style={{ minHeight: itemHeight, overflow: 'hidden' }}
                avatar={getBookAvatar()}
                title={
                    <BookItemTitle
                        book={book}
                        compact={compact}
                        linkedSections={linkedSections}
                        exist={exist}
                        sortableDragging={sortableDragging}
                        embeddedInSortableRow={embeddedInSortableRow}
                        sortableNoDragProps={sortableNoDragProps}
                        onTitleClick={handleBookClick}
                        titleTooltip={packageSection.active ? packageActionLabel : null}
                        actions={isMobile ? null : listActions}
                    />
                }
                description={
                    isAdding ? (
                        <small>adding book to package...</small>
                    ) : isRemoving ? (
                        <small>removing book from package...</small>
                    ) : (
                        <BookItemContentWrapper
                            $isPrivate={book.PrivacyLevelName === 'User'}
                            $minHeight={descriptionMinHeight}
                            $isMobile={isMobile}
                        >
                            <div className="book-item-content-left">
                                <div className="author-container">
                                    {book.PrivacyLevelName === 'User' && <NwIcon icon={byPrefixAndName.fasl['lock']} />}
                                    <CreatedBy object={book} short textonly />
                                </div>
                                {!compact && (
                                    <div className="description-container">
                                        <NwEllipsis
                                            rows={descriptionRows}
                                            text={book.Description}
                                            alwaysModal
                                            popover
                                            popovertitle="Description"
                                            noFontSize
                                        />
                                    </div>
                                )}
                            </div>
                            {!isMobile && bookButtons && <div className="book-item-content-right">{bookButtons}</div>}
                        </BookItemContentWrapper>
                    )
                }
            />
            {isMobile && (
                <MobileActionsRow>
                    <div>{listActions}</div>
                    {bookButtons && <div className="icons-container">{bookButtons}</div>}
                </MobileActionsRow>
            )}
        </ListItem>
    );
};

export default BookItem;
